import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ProjectCategoryOption } from '@features/quotation-intake/types/quotationIntake.types';

interface ProjectCategoryComboboxProps {
  value: { id: string; name: string | null } | null;
  options: ProjectCategoryOption[];
  onSelect: (category: ProjectCategoryOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

export function ProjectCategoryCombobox({
  value,
  options,
  onSelect,
  placeholder = 'Select project category…',
  disabled,
  id,
}: ProjectCategoryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, options]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, []);

  const commit = useCallback(
    (opt: ProjectCategoryOption) => {
      onSelect(opt);
      setOpen(false);
      setQuery('');
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filtered.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = filtered[activeIndex];
      if (picked) commit(picked);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const selectedLabel = value?.name ?? '';
  const hasOptions = options.length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled || !hasOptions}
          title={selectedLabel || undefined}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate text-left">
            {selectedLabel || (hasOptions ? placeholder : 'Loading…')}
          </span>
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Clear project category"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(24rem,90vw)] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search categories…"
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {filtered.length > 0 ? (
            <ul role="listbox" className="py-1">
              {filtered.map((opt, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = value?.id === opt.id;
                return (
                  <li key={opt.id} role="option" aria-selected={isActive}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => commit(opt)}
                      title={opt.name}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        isActive && 'bg-accent',
                      )}
                    >
                      <span className="truncate">{opt.name}</span>
                      {isSelected && <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {hasOptions ? 'No categories match your search' : 'No categories available'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
